import React, { useEffect, useMemo, useRef, useState } from 'react';
import { runWhatIf } from '../api';
import useScenarios from '../hooks/useScenarios';
import GrowthChart from './charts/GrowthChart';
import { fmtPct, fmtNum, signClass } from '../utils/format';
import { track } from '../utils/analytics';
import './LabSection.css';

// The lab: three questions the rest of the note cannot answer.
//   What if I change the mix?      → the same risk figures for a proposed set of holdings
//   What did past selloffs do?     → replayed on today's book, in today's money
//   Did the optimiser's advice work? → last year's weights held through this year

const WHATIF_ROWS = [
  ['annual_return', 'Expected return · ann.', 'pct'],
  ['volatility', 'Volatility · ann.', 'pct'],
  ['sharpe_ratio', 'Sharpe', 'ratio'],
  ['beta', 'Beta', 'ratio'],
  ['max_drawdown', 'Max drawdown', 'pct100'],
  ['var_95', 'VaR 95 · one day', 'money'],
];

function metricCell(kind, m, money) {
  if (!m) return '—';
  if (m.locked) return <span className="dd-muted">Pro</span>;
  const v = m.value;
  if (kind === 'pct') return `${fmtNum(v, 1)}%`;
  if (kind === 'pct100') return fmtPct(v * 100, { sign: false });
  if (kind === 'money') return money(m.money ?? 0);
  return fmtNum(v, 2);
}

function deltaCell(kind, key, deltas, money) {
  const d = deltas?.[key];
  if (d == null) return '';
  // For risk figures, lower is better; colour the sign accordingly.
  const lowerIsBetter = key === 'volatility' || key === 'beta';
  const good = lowerIsBetter ? d < 0 : d > 0;
  const cls = d === 0 ? '' : good ? 'dd-gain' : 'dd-loss';
  if (kind === 'pct') return <span className={cls}>{fmtPct(d)}</span>;
  if (kind === 'pct100') return <span className={cls}>{fmtPct(d * 100)}</span>;
  if (kind === 'money') return '';
  return <span className={cls}>{d > 0 ? '+' : ''}{fmtNum(d, 2)}</span>;
}

function toRows(holdings) {
  return (holdings || []).filter((h) => h.ticker).map((h) => ({ ticker: String(h.ticker).toUpperCase(), quantity: String(h.quantity ?? '') }));
}

export default function LabSection({ user, userProfile, rows = [], baseCurrency = 'USD', money, optimizer, }) {
  const scen = useScenarios(user, userProfile);
  const requested = useRef(false);

  // ── What-if state ──
  const [proposed, setProposed] = useState(() => toRows(userProfile?.holdings));
  const [newTicker, setNewTicker] = useState('');
  const [newQty, setNewQty] = useState('');
  const [whatif, setWhatif] = useState(null);
  const [whatifBusy, setWhatifBusy] = useState(false);
  const [whatifError, setWhatifError] = useState(null);
  const [shock, setShock] = useState(10);

  useEffect(() => {
    setProposed(toRows(userProfile?.holdings));
    setWhatif(null);
  }, [userProfile?.holdings]);

  useEffect(() => {
    if (!scen.hasMinHoldings || !scen.scenariosHydrated || scen.scenarios || scen.scenariosLoading || requested.current) return;
    requested.current = true;
    scen.fetchScenarios();
  }, [scen]);

  const currentByTicker = useMemo(() => {
    const m = {};
    (userProfile?.holdings || []).forEach((h) => { m[String(h.ticker || '').toUpperCase()] = Number(h.quantity) || 0; });
    return m;
  }, [userProfile?.holdings]);

  const priceOf = useMemo(() => {
    // Base-currency value per share, from the note's live rows.
    const m = {};
    rows.forEach((r) => { if (r.ticker && Number(r.quantity) > 0 && Number(r.current_value) > 0) m[r.ticker] = r.current_value / r.quantity; });
    return m;
  }, [rows]);
  const totalValue = rows.reduce((s, r) => s + (Number(r.current_value) || 0), 0);

  const applyWeights = (weights, label) => {
    if (!weights || !totalValue) return;
    const next = Object.entries(weights)
      .filter(([, w]) => w > 0.05)
      .map(([t, w]) => {
        const p = priceOf[t];
        return { ticker: t, quantity: p ? String(Math.max(1, Math.round((w / 100) * totalValue / p))) : String(currentByTicker[t] || '') };
      });
    setProposed(next);
    setWhatif(null);
    track('whatif_preset', { preset: label });
  };

  const setQty = (i, q) => setProposed((p) => p.map((r, j) => (j === i ? { ...r, quantity: q } : r)));
  const removeRow = (i) => setProposed((p) => p.filter((_, j) => j !== i));
  const addRow = (e) => {
    e.preventDefault();
    const t = newTicker.trim().toUpperCase();
    const q = parseFloat(newQty);
    if (!/^[A-Z][A-Z0-9.&-]{0,11}$/.test(t) || !(q > 0)) return;
    setProposed((p) => {
      const i = p.findIndex((r) => r.ticker === t);
      if (i >= 0) return p.map((r, j) => (j === i ? { ...r, quantity: String(q) } : r));
      return [...p, { ticker: t, quantity: String(q) }];
    });
    setNewTicker('');
    setNewQty('');
  };

  const compare = async () => {
    const clean = proposed.map((r) => ({ ticker: r.ticker, quantity: parseFloat(r.quantity) || 0 })).filter((r) => r.quantity > 0);
    if (!clean.length) { setWhatifError('Add at least one holding to the proposed mix.'); return; }
    setWhatifBusy(true);
    setWhatifError(null);
    try {
      const res = await runWhatIf({
        holdings: userProfile.holdings,
        proposed_holdings: clean,
        user_profile: { ...userProfile, isPremium: user?.isPremium || false },
        user_id: user?.uid,
      });
      if (res.error) setWhatifError(res.error + (res.missing?.length ? ` (${res.missing.join(', ')})` : ''));
      else setWhatif(res);
      track('whatif_run', { holdings: clean.length });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setWhatifError((typeof detail === 'string' ? detail : detail?.message) || 'Could not compare these holdings right now.');
    } finally {
      setWhatifBusy(false);
    }
  };

  const o = optimizer?.optimization;
  const stress = scen.scenarios?.stress;
  const bt = scen.scenarios?.backtest;
  const beta = stress?.portfolio_beta;
  const stressValue = stress?.value || totalValue;
  const topRisk = (rc) => {
    if (!rc) return null;
    const [t, v] = Object.entries(rc).sort((a, b) => b[1] - a[1])[0] || [];
    return t ? `${t} drives ${fmtNum(v, 0)}% of the risk` : null;
  };

  return (
    <section id="lab" className="pn-section lab">
      <div className="pn-section-head">
        <div className="dd-eyebrow">Lab · what if</div>
        <div className="pn-section-tools dd-mono dd-muted">
          {whatif?.usage?.daily_limit != null && `${whatif.usage.remaining} of ${whatif.usage.daily_limit} comparisons left today`}
        </div>
      </div>

      {/* ── What-if ── */}
      <div className="lab-grid">
        <div className="lab-editor">
          <p className="lab-lead">Change the share counts, add a ticker, or start from one of the optimiser's answers. Everything is recomputed on the same year of prices, so the two columns are comparable.</p>
          <table className="pn-table dd-num lab-table">
            <thead><tr><th>Ticker</th><th className="r">Now</th><th className="r">Proposed</th><th aria-label="Remove" /></tr></thead>
            <tbody>
              {proposed.map((r, i) => (
                <tr key={r.ticker}>
                  <td className="dd-mono">{r.ticker}</td>
                  <td className="r dd-muted">{currentByTicker[r.ticker] ? fmtNum(currentByTicker[r.ticker], 0) : '—'}</td>
                  <td className="r"><input className="lab-qty dd-mono" type="number" min="0" step="1" value={r.quantity} onChange={(e) => setQty(i, e.target.value)} aria-label={`${r.ticker} proposed shares`} /></td>
                  <td className="r"><button type="button" className="lab-x" onClick={() => removeRow(i)} aria-label={`Remove ${r.ticker}`}>×</button></td>
                </tr>
              ))}
              {Object.keys(currentByTicker).filter((t) => !proposed.some((r) => r.ticker === t)).map((t) => (
                <tr key={`sold-${t}`} className="lab-sold">
                  <td className="dd-mono">{t}</td><td className="r dd-muted">{fmtNum(currentByTicker[t], 0)}</td><td className="r dd-muted">sold</td>
                  <td className="r"><button type="button" className="pn-textbtn" onClick={() => setProposed((p) => [...p, { ticker: t, quantity: String(currentByTicker[t]) }])}>undo</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <form className="lab-add" onSubmit={addRow}>
            <input className="lab-add-ticker dd-mono" placeholder="Add ticker" value={newTicker} onChange={(e) => setNewTicker(e.target.value)} aria-label="Ticker to add" />
            <input className="lab-add-qty dd-mono" type="number" min="0" placeholder="Shares" value={newQty} onChange={(e) => setNewQty(e.target.value)} aria-label="Shares to add" />
            <button type="submit" className="dd-btn-ghost">Add</button>
          </form>
          <div className="lab-presets dd-mono">
            <span className="dd-muted">Start from:</span>
            <button type="button" className="pn-textbtn" onClick={() => { setProposed(toRows(userProfile?.holdings)); setWhatif(null); }}>what I hold</button>
            {o?.max_sharpe?.weights && <button type="button" className="pn-textbtn" onClick={() => applyWeights(o.max_sharpe.weights, 'max_sharpe')}>max Sharpe</button>}
            {o?.min_volatility?.weights && <button type="button" className="pn-textbtn" onClick={() => applyWeights(o.min_volatility.weights, 'min_vol')}>min volatility</button>}
            {o?.risk_parity?.weights && <button type="button" className="pn-textbtn" onClick={() => applyWeights(o.risk_parity.weights, 'risk_parity')}>risk parity</button>}
          </div>
          <button type="button" className="dd-btn lab-run" onClick={compare} disabled={whatifBusy}>{whatifBusy ? 'Comparing…' : 'Compare'}</button>
          {whatifError && <p className="pn-error">{whatifError}</p>}
        </div>

        <div className="lab-result">
          {!whatif ? (
            <div className="lab-placeholder dd-mono dd-muted">The comparison appears here: value, return, volatility, Sharpe, beta, drawdown and one-day VaR, now against proposed.</div>
          ) : (
            <>
              <table className="pn-table dd-num lab-compare">
                <thead><tr><th /><th className="r">Now</th><th className="r">Proposed</th><th className="r">Change</th></tr></thead>
                <tbody>
                  <tr>
                    <td>Value</td>
                    <td className="r">{money(whatif.current?.value)}</td>
                    <td className="r">{money(whatif.proposed?.value)}</td>
                    <td className={`r ${signClass(whatif.deltas?.value)}`}>{whatif.deltas?.value != null ? (whatif.deltas.value > 0 ? '+' : whatif.deltas.value < 0 ? '−' : '') + money(Math.abs(whatif.deltas.value)) : ''}</td>
                  </tr>
                  {WHATIF_ROWS.map(([key, label, kind]) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td className="r">{metricCell(kind, whatif.current?.metrics?.[key], money)}</td>
                      <td className="r">{metricCell(kind, whatif.proposed?.metrics?.[key], money)}</td>
                      <td className="r">{deltaCell(kind, key, whatif.deltas, money)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="lab-notes dd-mono dd-muted">
                {topRisk(whatif.current?.risk_contribution) && <span>Now: {topRisk(whatif.current.risk_contribution)}.</span>}
                {topRisk(whatif.proposed?.risk_contribution) && <span>Proposed: {topRisk(whatif.proposed.risk_contribution)}.</span>}
                {whatif.proposed?.missing?.length > 0 && <span>No history for {whatif.proposed.missing.join(', ')}; left out.</span>}
                {whatif.tier_limited && <span>Drawdown and VaR are Pro figures. <a href="/premium">See Pro</a></span>}
                <span>Value differences come from the shares you changed, at today's prices. Nothing here is a trade.</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Stress ── */}
      <div className="lab-sub">
        <div className="pn-section-head">
          <div className="dd-eyebrow">Stress · past selloffs replayed on this book</div>
          <div className="pn-section-tools dd-mono dd-muted">
            {scen.scenariosRateLimitInfo?.daily_limit != null && `${scen.scenariosRateLimitInfo.remaining} of ${scen.scenariosRateLimitInfo.daily_limit} runs left today`}
            <button type="button" className="pn-textbtn" onClick={() => scen.refreshScenarios()} disabled={scen.scenariosLoading}>{scen.scenariosLoading ? 'Replaying…' : 'Rerun'}</button>
          </div>
        </div>
        {scen.scenariosError && <p className="pn-error">{String(scen.scenariosError)}</p>}
        {!stress && !scen.scenariosError && <p className="dd-muted">{scen.scenariosLoading ? 'Replaying five selloffs on your holdings…' : 'Scenarios will appear here.'}</p>}
        {stress?.error && <p className="dd-muted">{stress.error}</p>}
        {stress?.scenarios?.length > 0 && (
          <>
            <div className="pn-scroll">
              <table className="pn-table dd-num lab-stress">
                <thead><tr><th>Scenario</th><th>Window</th><th className="r">S&amp;P 500</th><th className="r">Your book</th><th className="r">At today's value</th><th>Worst holding</th><th className="r">S&amp;P recovery</th></tr></thead>
                <tbody>
                  {stress.scenarios.map((s) => (
                    <tr key={s.id}>
                      <td><strong>{s.name}</strong><div className="dd-muted lab-cause">{s.cause}</div></td>
                      <td className="dd-mono dd-muted lab-window">{s.start} → {s.end}</td>
                      <td className={`r ${signClass(s.index_return_pct)}`}>{fmtPct(s.index_return_pct)}</td>
                      <td className={`r ${signClass(s.portfolio_return_pct)}`}><strong>{fmtPct(s.portfolio_return_pct)}</strong></td>
                      <td className={`r ${signClass(s.portfolio_money)}`}>{s.portfolio_money < 0 ? '−' : '+'}{money(Math.abs(s.portfolio_money))}</td>
                      <td><span className="dd-mono">{s.worst_holding?.ticker}</span> <span className={signClass(s.worst_holding?.return_pct)}>{fmtPct(s.worst_holding?.return_pct)}</span>{s.proxied?.length > 0 && <span className="dd-muted lab-proxy" title={`${s.proxied.join(', ')} did not exist yet; estimated from beta`}> · {s.proxied.length} est.</span>}</td>
                      <td className="r dd-muted">{s.index_recovery_days != null ? `${s.index_recovery_days} days` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="lab-shock">
              <label htmlFor="lab-shock" className="dd-eyebrow">If the S&amp;P 500 fell</label>
              <input id="lab-shock" type="range" min="5" max="50" step="5" value={shock} onChange={(e) => setShock(Number(e.target.value))} />
              <span className="dd-serif dd-num lab-shock-val">{shock}%</span>
              <span className="lab-shock-out">your book would move about <strong className="dd-loss">−{fmtNum((beta || 1) * shock, 1)}%</strong>, roughly <strong className="dd-loss">−{money((beta || 1) * shock / 100 * stressValue)}</strong>, at a beta of {fmtNum(beta, 2)}.</span>
            </div>
            <p className="lab-notes dd-mono dd-muted">Replays use each holding's own prices over the window, converted to {baseCurrency}. Holdings that did not exist yet are estimated from their one-year beta. History is not a forecast.</p>
          </>
        )}
      </div>

      {/* ── Backtest ── */}
      <div className="lab-sub">
        <div className="pn-section-head">
          <div className="dd-eyebrow">Backtest · would the optimiser's advice have worked?</div>
        </div>
        {!bt && !scen.scenariosError && <p className="dd-muted">{scen.scenariosLoading ? 'Fitting last year, holding through this year…' : ''}</p>}
        {bt?.error && <p className="dd-muted">{bt.error}{bt.excluded?.length ? ` Too young: ${bt.excluded.join(', ')}.` : ''}</p>}
        {bt?.series && (
          <div className="lab-bt">
            <div>
              <GrowthChart dates={bt.dates} series={bt.series} order={['you', 'max_sharpe', 'min_vol', 'spx']} labels={{ you: 'What you hold', max_sharpe: 'Max Sharpe weights', min_vol: 'Min volatility weights', spx: 'S&P 500' }} height={260} ariaLabel="Growth of 100 over the last year" />
            </div>
            <div className="lab-bt-side">
              <table className="pn-table dd-num lab-bt-stats">
                <thead><tr><th /><th className="r">Return</th><th className="r">Vol</th><th className="r">Worst dd</th><th className="r">Sharpe</th></tr></thead>
                <tbody>
                  {[['you', 'What you hold'], ['max_sharpe', 'Max Sharpe'], ['min_vol', 'Min volatility'], ['spx', 'S&P 500']].map(([k, label]) => (
                    <tr key={k}>
                      <td>{label}</td>
                      <td className={`r ${signClass(bt.stats[k]?.return_pct)}`}>{fmtPct(bt.stats[k]?.return_pct)}</td>
                      <td className="r">{fmtNum(bt.stats[k]?.volatility_pct, 1)}%</td>
                      <td className="r dd-loss">{fmtPct(bt.stats[k]?.max_drawdown_pct, { sign: false })}</td>
                      <td className="r">{fmtNum(bt.stats[k]?.sharpe, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="lab-notes dd-mono dd-muted">
                Weights were fitted on {bt.fit_window.start} to {bt.fit_window.end} and then held, untouched, from {bt.test_window.start} to {bt.test_window.end}. They never saw the test year, so this is the honest version of "if you had followed the optimiser".
                {bt.excluded?.length ? ` ${bt.excluded.join(', ')} left out: less than two years of history.` : ''}
                {' '}{bt.stats.max_sharpe?.return_pct > bt.stats.you?.return_pct ? 'This year the optimiser\'s weights beat what you hold.' : 'This year what you hold beat the optimiser\'s weights; one year is one draw.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
